<template>
  <div
    class="leg-row flex gap-3 py-3 border-b border-gray-200 transition-colors duration-700"
    :class="{ 'just-added': justAdded }"
  >
    <div class="flex-1 min-w-0">
      <div class="flex items-start justify-between gap-2">
        <span class="flex items-start gap-2 min-w-0">
          <!-- the leg's team crest; both, overlapping, for a draw or a goals bet -->
          <span v-if="crests.length" class="flex-none flex items-center pt-px" aria-hidden="true">
            <img
              v-for="(src, i) in crests"
              :key="src"
              :src="src"
              alt=""
              class="crest w-5 h-5 object-contain"
              :class="{ '-ml-2': i > 0 }"
            />
          </span>
          <span class="text-sm font-semibold text-gray-800 min-w-0">
            {{ leg.label || (priced && priced.label) || "Loading…" }}
          </span>
        </span>
        <button
          type="button"
          class="text-brandGrey text-base leading-none px-1 -mr-1"
          :aria-label="`Remove ${leg.label || 'leg'} from slip`"
          v-on:click="$emit('remove', leg.id)"
        >
          ×
        </button>
      </div>
      <div class="text-xxs text-brandGrey">
        {{ leg.match || (priced && priced.match) }}
        <template v-if="kickoffText"> · {{ kickoffText }}</template>
        <template v-if="priced && priced.competition">
          · {{ priced.competition }}</template
        >
      </div>
      <!-- four even columns, each label over its figure; hover the best odds for the bookmaker -->
      <div
        class="leg-stats grid grid-cols-4 gap-x-2 mt-2 tabular-nums"
        :class="{ 'blur-sm select-none': locked }"
        :aria-hidden="locked ? 'true' : null"
      >
        <div>
          <div class="label">AI chance</div>
          <div class="stat">{{ shown(priced && priced.p, (v) => `${Math.round(v)}%`) }}</div>
        </div>
        <div>
          <div class="label">Fair odds</div>
          <div class="stat">{{ shown(priced && priced.fair_odds, (v) => format(v)) }}</div>
        </div>
        <div class="min-w-0">
          <div class="label">Best odds</div>
          <div class="stat">
            <span
              :title="priced && priced.odds && priced.bookmaker ? `with ${priced.bookmaker}` : null"
              :class="{ 'cursor-help': priced && priced.bookmaker }"
              >{{ priced && priced.odds ? format(priced.odds) : "–" }}</span
            >
          </div>
        </div>
        <div>
          <div class="label">Value</div>
          <div class="stat">
            <span v-if="priced && priced.edge !== null && priced.edge !== undefined" class="chip" :class="chipClass">{{
              edgeText
            }}</span>
            <template v-else>–</template>
          </div>
        </div>
      </div>
      <div v-if="flagText.length" class="text-xxs mt-1 text-gray-700">
        <span v-for="flag in flagText" :key="flag" class="mr-2">⚠ {{ flag }}</span>
      </div>
      <div
        v-if="suggestion"
        class="swap-box mt-2 rounded-lg p-2 text-xs bg-[#dcdcff]"
      >
        <div class="flex items-center justify-between mb-1">
          <span class="label ai-glow">Suggested alternative</span>
          <!-- not wanted: hide it for this leg -->
          <button
            type="button"
            class="-m-2 p-2 text-gray-500 leading-none"
            aria-label="Hide the suggested alternative"
            title="Hide"
            v-on:click="dismiss"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
              stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>
        <div class="text-sm font-semibold text-gray-800">{{ priced.suggestion.label }}</div>
        <!-- the same four columns as the leg above, centred in the box -->
        <div class="leg-stats leg-stats--centred grid grid-cols-4 gap-x-2 mt-1 tabular-nums">
          <template v-if="priced.suggestion.locked">
            <div class="col-start-3">
              <div class="label">Best odds</div>
              <div class="stat">{{ format(priced.suggestion.odds) }}</div>
            </div>
            <div>
              <div class="label">Value</div>
              <div class="stat"><span class="chip value">better</span></div>
            </div>
          </template>
          <template v-else>
            <div>
              <div class="label">AI chance</div>
              <div class="stat">{{ Math.round(priced.suggestion.p) }}%</div>
            </div>
            <div>
              <div class="label">Fair odds</div>
              <div class="stat">{{ format(100 / priced.suggestion.p) }}</div>
            </div>
            <div>
              <div class="label">Best odds</div>
              <div class="stat">{{ format(priced.suggestion.odds) }}</div>
            </div>
            <div>
              <div class="label">Value</div>
              <div class="stat"><span class="chip value">{{ formatEdge(priced.suggestion.edge) }}</span></div>
            </div>
          </template>
        </div>
        <router-link
          v-if="priced.suggestion.locked"
          to="/signup/"
          class="block mt-1.5 text-xxs text-blue-700 underline"
          v-on:click.native="upgradeClick"
          >Upgrade to see our numbers →</router-link
        >
        <!-- the one action, on its own line: the design language's solid blue button -->
        <div class="flex justify-center mt-2">
          <button
            type="button"
            class="rounded-lg bg-blue-700 text-white uppercase font-bold text-xs px-6 py-1.5"
            v-on:click="$emit('swap', { oldId: leg.id, suggestion: priced.suggestion, leg })"
          >
            Swap
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import { formatOdds, formatEdge } from "../../odds";
import { trackFeature } from "../../analytics";

const FLAGS = {
  started: "started",
  lineup_not_confirmed: "lineup not confirmed",
  doubtful: "doubtful",
  injured: "injured",
  suspended: "suspended",
  same_match: "same match as another leg",
  no_price: "no bookmaker price yet",
  no_model: "bookmaker's price only, no AI prediction yet",
  unknown: "no longer in our data",
  not_in_tier: "not in your plan's leagues",
};

export default {
  name: "LegRow",
  props: {
    leg: { type: Object, required: true },
    priced: { type: Object, default: null },
    locked: { type: Boolean, default: false },
    justAdded: { type: Boolean, default: false },
  },
  data() {
    return {
      // the suggestion the user closed; a different one later still shows
      dismissed: null,
    };
  },
  computed: {
    crests() {
      const logos = (this.priced && this.priced.logos) || this.leg.logos || {};
      const side = this.leg.side || (this.priced && this.priced.side);
      if (side === "home" || side === "away") return logos[side] ? [logos[side]] : [];
      return [logos.home, logos.away].filter(Boolean);
    },
    suggestion() {
      const s = this.priced && this.priced.suggestion;
      return s && s.id !== this.dismissed ? s : null;
    },
    oddsFormat() {
      return this.$store.getters.preferences.odds_format;
    },
    kickoffText() {
      const ts = this.leg.kickoff || (this.priced && this.priced.kickoff);
      return ts ? dayjs.unix(ts).format("ddd HH:mm") : "";
    },
    // on the slip any positive value is green and any negative red, as the shown % reads
    chipClass() {
      const edge = this.priced && this.priced.edge;
      if (edge === null || edge === undefined) return "neutral";
      const shown = Math.round(edge);
      return shown > 0 ? "value" : shown < 0 ? "novalue" : "neutral";
    },
    edgeText() {
      return formatEdge(this.priced && this.priced.edge);
    },
    flagText() {
      const flags = (this.priced && this.priced.flags) || [];
      return flags.map((f) => FLAGS[f] || f.replace(/_/g, " "));
    },
  },
  methods: {
    dismiss() {
      this.dismissed = this.priced.suggestion.id;
      trackFeature("betslip_suggestion_dismiss", "betslip");
    },
    upgradeClick() {
      trackFeature("betslip_upgrade_click", "betslip", { from: "swap" });
    },
    format(decimal) {
      return formatOdds(decimal, this.oddsFormat);
    },
    formatEdge,
    shown(value, fn) {
      if (this.locked) return "??";
      return value === null || value === undefined ? "–" : fn(value);
    },
  },
};
</script>

<style scoped>
/* crests sit on a white disc so the overlapping pair stays readable */
.crest {
  background: #fff;
  border-radius: 9999px;
  padding: 1px;
  box-shadow: 0 0 0 1px #fff;
}
/* the homepage's AI gradient (brand-gradient), on the suggestion's label */
.label.ai-glow {
  font-weight: 600;
  background: linear-gradient(90deg, #1966ff, #5036ff, #1554ff, #402fff);
  background-size: 300% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: ai-shift 3s ease-in-out infinite;
}
@keyframes ai-shift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}
@media (prefers-reduced-motion: reduce) {
  .label.ai-glow {
    animation: none;
  }
}
</style>
